/**
 * The card-as-pager's input handling, graduated from the three houses' copied handler blocks
 * (plan 2026_09_23_pack_stage_coda.md § The shared pieces). The house supplies `advance`; this
 * returns the handlers its card binds:
 *
 *   - horizontal touch swipe with the axis test. Nothing is preventDefault-ed: a vertical drag must
 *     keep scrolling the page, and the axis test is what separates the two;
 *   - the synthesized-click suppression: a completed swipe swallows the click the browser may
 *     synthesize on touchend, so one gesture never counts as both a swipe and a tap;
 *   - ←/→ read off the card rather than off each zone: the event bubbles from whichever control
 *     holds focus, so both keys work from either zone.
 *
 * Events whose target is a typing surface (input, textarea, contenteditable) are ignored — for keys
 * because ←/→ there move the caret, for touch because a caret drag is a horizontal gesture too. No
 * house had a field on its card before the coda; Toile's coda holds one.
 *
 * The logic is `pagerGestureHandlers`, a plain function over a state box, so it is testable
 * without a DOM (`__tests__/usePagerGestures.spec.ts`); the hook only owns the box.
 */
import {useRef} from 'react'

/** Wide enough that a thumb resting on the card mid-scroll doesn't page it, narrow enough to fire
 *  inside the width of a phone. */
export const SWIPE_MIN_PX = 40

export type PagerGestureMethod = 'tap' | 'swipe' | 'arrow'
export type PagerAdvance = (delta: 1 | -1, method: PagerGestureMethod) => void

type Point = {clientX: number; clientY: number}
export type TouchLike = {
    touches: ArrayLike<Point>
    changedTouches: ArrayLike<Point>
    target: EventTarget | null
}
export type KeyLike = {key: string; target: EventTarget | null; preventDefault(): void}

export type PagerGestureState = {start: {x: number; y: number} | null; swipeConsumedClick: boolean}

export type PagerGestures = {
    onTouchStart: (e: TouchLike) => void
    onTouchEnd: (e: TouchLike) => void
    onKeyDown: (e: KeyLike) => void
    /** A zone's click, `delta` its direction. */
    onZoneClick: (delta: 1 | -1) => void
}

export function isTypingTarget(target: EventTarget | null): boolean {
    if (!target || typeof (target as {tagName?: unknown}).tagName !== 'string') return false
    const el = target as HTMLElement
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true
}

export function pagerGestureHandlers(box: {current: PagerGestureState}, advance: PagerAdvance): PagerGestures {
    return {
        onTouchStart(e) {
            // Cleared here, not on a timer: browsers only sometimes synthesize the click a swipe
            // would consume, so the flag is dropped by the next interaction rather than by the one
            // it was raised for.
            box.current.swipeConsumedClick = false
            const t = e.touches[0]
            box.current.start = t && !isTypingTarget(e.target) ? {x: t.clientX, y: t.clientY} : null
        },
        onTouchEnd(e) {
            const start = box.current.start
            box.current.start = null
            const t = e.changedTouches[0]
            if (!start || !t) return
            const dx = t.clientX - start.x
            const dy = t.clientY - start.y
            if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return
            box.current.swipeConsumedClick = true
            advance(dx < 0 ? 1 : -1, 'swipe')
        },
        onKeyDown(e) {
            if (isTypingTarget(e.target)) return
            if (e.key === 'ArrowRight') {
                e.preventDefault()
                advance(1, 'arrow')
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault()
                advance(-1, 'arrow')
            }
        },
        onZoneClick(delta) {
            if (box.current.swipeConsumedClick) {
                box.current.swipeConsumedClick = false
                return
            }
            advance(delta, 'tap')
        },
    }
}

export function usePagerGestures(advance: PagerAdvance): PagerGestures {
    const box = useRef<PagerGestureState>({start: null, swipeConsumedClick: false})
    return pagerGestureHandlers(box, advance)
}
